/**
 * BulkActionCommentModal
 *
 * Confirmation dialog used both by the queue's bulk approve / reject flow and
 * by the single-drift detail drawer. Restyled to the editorial palette
 * (paper / navy / rule + green-deep / red-deep semantic accents) so it sits
 * naturally over the queue and the drawer instead of the older dark-theme
 * popover.
 *
 * Reject defaults to optional; pass `requireComment` to lock the confirm
 * button until the user gives a reason — used so rejections always carry a
 * note in the audit trail.
 */

import { useState, useEffect, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { DriftStatus } from '@/types'

interface BulkActionCommentModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (comment: string) => void
  actionStatus: DriftStatus
  selectedCount: number
  isProcessing: boolean
  /** When true, an empty comment cannot be submitted (used for rejections so
   *  the audit trail always carries a reason). Defaults to false. */
  requireComment?: boolean
}

/** Verb shown in the headline + footer button. */
const statusLabels: Record<string, string> = {
  Approved: 'Approve',
  Rejected: 'Reject',
  New: 'Mark as new',
  Ignored: 'Ignore',
  AwaitingRemediation: 'Mark awaiting remediation',
  Remediated: 'Mark remediated',
}

interface StatusVisual {
  /** CSS color used for the italic accent verb in the headline + body. */
  accent: string
  /** Solid fill color for the confirm CTA. */
  fill: string
  /** Drop shadow paired with the fill (matches the soft-press feel from the
   *  queue's PrimaryButton). */
  shadow: string
}

const statusVisual: Record<string, StatusVisual> = {
  Approved: {
    accent: 'var(--color-green-deep, #4A9B6E)',
    fill: 'var(--color-green-deep, #4A9B6E)',
    shadow: '0 4px 14px rgba(74,155,110,0.32)',
  },
  Rejected: {
    accent: 'var(--color-red-deep, #C84B4B)',
    fill: 'var(--color-red-deep, #C84B4B)',
    shadow: '0 4px 14px rgba(200,75,75,0.32)',
  },
  New: {
    accent: 'var(--color-blue-deep, #7BA8C4)',
    fill: 'var(--color-blue-deep, #7BA8C4)',
    shadow: '0 4px 14px rgba(123,168,196,0.32)',
  },
  Ignored: {
    accent: 'var(--color-mute, #6B7280)',
    fill: 'var(--color-mute, #6B7280)',
    shadow: '0 4px 14px rgba(107,114,128,0.28)',
  },
  AwaitingRemediation: {
    accent: 'var(--color-sand-deep, #C9AD80)',
    fill: 'var(--color-sand-deep, #C9AD80)',
    shadow: '0 4px 14px rgba(201,173,128,0.35)',
  },
  Remediated: {
    accent: 'var(--color-green-deep, #4A9B6E)',
    fill: 'var(--color-green-deep, #4A9B6E)',
    shadow: '0 4px 14px rgba(74,155,110,0.32)',
  },
}

export default function BulkActionCommentModal({
  isOpen,
  onClose,
  onConfirm,
  actionStatus,
  selectedCount,
  isProcessing,
  requireComment = false,
}: BulkActionCommentModalProps) {
  const [comment, setComment] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isOpen) {
      setComment('')
      // Focus textarea after modal renders
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const trimmed = comment.trim()
  const missingRequiredComment = requireComment && trimmed.length === 0

  const handleConfirm = () => {
    if (missingRequiredComment) return
    onConfirm(trimmed)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl+Enter / Cmd+Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleConfirm()
    }
  }

  const label = statusLabels[actionStatus] ?? actionStatus
  const visual = statusVisual[actionStatus] ?? statusVisual.Approved
  const placeholder =
    actionStatus === 'Rejected'
      ? 'Tell the team why this change is being rejected.'
      : 'e.g. Approved per change request CR-1234...'
  const driftWord = `drift${selectedCount > 1 ? 's' : ''}`
  const eyebrow = `Drift action · ${selectedCount} ${driftWord}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(33,41,56,0.30)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-[12px] border shadow-[0_18px_48px_rgba(33,41,56,0.18)]"
        style={{
          backgroundColor: 'var(--color-paper, #fff)',
          borderColor: 'var(--color-rule, #E6E1D5)',
        }}
      >
        {/* Header — eyebrow + italic-mixed headline; close X on the right. */}
        <div
          className="flex items-start justify-between gap-4 px-6 pt-6 pb-5"
          style={{ borderBottom: '1px solid var(--color-rule, #E6E1D5)' }}
        >
          <div className="min-w-0">
            <div
              className="text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: 'var(--color-mute, #6B7280)' }}
            >
              {eyebrow}
            </div>
            <h3
              className="mt-1.5 font-display text-[20px] font-extrabold leading-[1.18] tracking-[-0.014em]"
              style={{ color: 'var(--color-navy, #212938)' }}
            >
              <em
                className="font-serif font-semibold italic"
                style={{ color: visual.accent }}
              >
                {label}
              </em>{' '}
              {selectedCount} {driftWord}.
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            aria-label="Close"
            className="-mr-2 -mt-2 rounded-full p-1.5 transition-colors hover:bg-[color:var(--color-paper-warm,#FBF8F2)] disabled:opacity-40"
            style={{ color: 'var(--color-mute, #6B7280)' }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body — context sentence, label, textarea, footer counter row. */}
        <div className="space-y-4 px-6 py-5">
          <p
            className="text-[13.5px] leading-[1.55]"
            style={{ color: 'var(--color-navy, #212938)' }}
          >
            You are about to{' '}
            <span className="font-semibold" style={{ color: visual.accent }}>
              {label.toLowerCase()}
            </span>{' '}
            <span className="font-semibold">{selectedCount}</span> selected{' '}
            {driftWord}.{' '}
            {requireComment
              ? 'Add a comment explaining why — it will be recorded in the audit trail.'
              : 'Add an optional comment that will be recorded in the audit trail.'}
          </p>

          <div>
            <label
              htmlFor="bulk-action-comment"
              className="block text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: 'var(--color-mute, #6B7280)' }}
            >
              Comment · {requireComment ? 'required' : 'optional'}
            </label>
            <textarea
              id="bulk-action-comment"
              ref={textareaRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isProcessing}
              rows={3}
              maxLength={500}
              placeholder={placeholder}
              className="mt-2 w-full rounded-[8px] border bg-[color:var(--color-paper-warm,#FBF8F2)] px-3 py-2.5 text-sm leading-[1.55] transition-colors focus:bg-[color:var(--color-paper,#fff)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-navy,#212938)]/20 disabled:opacity-60"
              style={{
                borderColor: 'var(--color-rule, #E6E1D5)',
                color: 'var(--color-navy, #212938)',
              }}
            />
            <div
              className="mt-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.16em]"
              style={{ color: 'var(--color-faint, #9CA3AF)' }}
            >
              <span>Press Ctrl+Enter to confirm</span>
              <span className="font-mono tabular-nums">{comment.length}/500</span>
            </div>
          </div>
        </div>

        {/* Footer — outline cancel + filled confirm. Confirm color tracks the
            action's semantic accent so reject reads red, approve green, etc. */}
        <div
          className="flex items-center justify-end gap-2.5 px-6 py-4"
          style={{
            backgroundColor: 'var(--color-paper-warm, #FBF8F2)',
            borderTop: '1px solid var(--color-rule, #E6E1D5)',
          }}
        >
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 rounded-full border bg-[color:var(--color-paper,#fff)] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors hover:border-[color:var(--color-navy,#212938)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[color:var(--color-rule,#E6E1D5)]"
            style={{
              borderColor: 'var(--color-rule, #E6E1D5)',
              color: 'var(--color-navy, #212938)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing || missingRequiredComment}
            className="inline-flex items-center gap-2 rounded-full px-[18px] py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            style={{
              backgroundColor: visual.fill,
              boxShadow: visual.shadow,
            }}
          >
            {isProcessing ? 'Processing…' : label}
          </button>
        </div>
      </div>
    </div>
  )
}
